import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function formatTimestamp(isoString) {
  const d = new Date(isoString);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `[${dd}${mm}${yy}][${hh}${min}${ss}]`;
}

export default function ShopPage({ darkMode = false }) {
  const [products, setProducts] = useState([]);
  const [progressModal, setProgressModal] = useState(null);
  const [descModal, setDescModal] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [vormerkModal, setVormerkModal] = useState(null);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(true);

  const bg = darkMode ? 'bg-gray-900' : 'bg-gray-50';
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const card = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';

  useEffect(() => {
    fetch('/api/shop/products')
      .then(r => r.json())
      .then(data => { setProducts(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  function openProgress(product) {
    fetch(`/api/shop/products/${product.id}/progress`)
      .then(r => r.json())
      .then(data => setProgressModal(data));
  }

  async function submitVormerken() {
    setEmailError('');
    if (!email || !email.includes('@')) {
      setEmailError('Bitte eine gültige E-Mail eingeben.');
      return;
    }
    try {
      const res = await fetch(`/api/shop/products/${vormerkModal.id}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        setEmailSent(true);
      } else {
        setEmailError('Fehler beim Speichern. Bitte erneut versuchen.');
      }
    } catch {
      setEmailError('Netzwerkfehler. Bitte erneut versuchen.');
    }
  }

  function closeVormerken() {
    setVormerkModal(null);
    setEmail('');
    setEmailSent(false);
    setEmailError('');
  }

  if (loading) {
    return (
      <div className={`min-h-screen ${bg} ${text} flex items-center justify-center`}>
        <div className="animate-pulse text-blue-500 text-xl">Lade Produkte...</div>
      </div>
    );
  }

  if (!products.length) {
    return (
      <div className={`min-h-screen ${bg} ${text} flex items-center justify-center`}>
        <p className="text-gray-500 text-lg">Noch keine Produkte verfügbar.</p>
      </div>
    );
  }

  const mdComponents = {
    h1: ({children}) => <h1 className="text-xl font-bold mb-2 mt-3">{children}</h1>,
    h2: ({children}) => <h2 className="text-lg font-bold mb-2 mt-3">{children}</h2>,
    h3: ({children}) => <h3 className="text-base font-semibold mb-1 mt-2">{children}</h3>,
    p:  ({children}) => <p className="mb-2">{children}</p>,
    ul: ({children}) => <ul className="list-disc list-inside mb-2 space-y-0.5">{children}</ul>,
    ol: ({children}) => <ol className="list-decimal list-inside mb-2 space-y-0.5">{children}</ol>,
    li: ({children}) => <li className="ml-2">{children}</li>,
    strong: ({children}) => <strong className="font-semibold">{children}</strong>,
    em: ({children}) => <em className="italic">{children}</em>,
    code: ({children}) => <code className={`px-1 py-0.5 rounded text-xs font-mono ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>{children}</code>,
    hr: () => <hr className={`my-3 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} />,
  };

  return (
    <div className={`min-h-screen ${bg} ${text} transition-colors duration-300`}>
      <div className="max-w-6xl mx-auto px-4 py-12">

        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-500 to-purple-600 text-transparent bg-clip-text">
          Shop
        </h1>
        <p className={`mb-10 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Unsere Produkte in Entwicklung – verfolge den Fortschritt live.
        </p>

        {/* Produkt-Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map(p => (
            <div key={p.id} className={`rounded-2xl border flex flex-col ${card} shadow-sm overflow-hidden`}>
              {p.image_url && (
                <img src={p.image_url} alt={p.name} className="w-full h-44 object-cover" />
              )}
              <div className="p-5 flex flex-col flex-1">
                <h2 className="text-lg font-bold mb-2">{p.name}</h2>
                {p.description && (
                  <div className="mb-4 flex-1">
                    <div className={`text-sm leading-relaxed line-clamp-4 overflow-hidden ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                        {p.description}
                      </ReactMarkdown>
                    </div>
                    <button
                      onClick={() => setDescModal(p)}
                      className="text-xs text-blue-500 hover:text-blue-400 mt-1 transition-colors"
                    >
                      mehr erfahren →
                    </button>
                  </div>
                )}

                {/* Progress-Balken */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs mb-1">
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Fortschritt</span>
                    <span className="font-semibold text-blue-500">{p.progress_percent}%</span>
                  </div>
                  <div className={`w-full rounded-full h-1.5 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div
                      className="bg-gradient-to-r from-blue-500 to-purple-600 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${p.progress_percent}%` }}
                    />
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => openProgress(p)}
                    className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors duration-200"
                  >
                    Fortschritt
                  </button>
                  <button
                    onClick={() => setVormerkModal(p)}
                    className={`px-4 py-2 rounded-xl border text-sm font-medium transition-colors duration-200 ${
                      darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Vormerken
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Fortschritt-Modal */}
      {progressModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setProgressModal(null)}>
          <div
            className={`w-full max-w-lg sm:max-w-2xl lg:max-w-4xl rounded-2xl p-6 sm:p-8 ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-bold ${text}`}>{progressModal.name}</h3>
              <button onClick={() => setProgressModal(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>

            {/* Fortschrittsbalken */}
            <div className="mb-5">
              <div className="flex justify-between text-sm mb-1">
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Fortschritt</span>
                <span className="font-bold text-blue-500">{progressModal.progress_percent}%</span>
              </div>
              <div className={`w-full rounded-full h-3 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <div
                  className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-700"
                  style={{ width: `${progressModal.progress_percent}%` }}
                />
              </div>
            </div>

            {/* Konsolen-Log mit inline Bildern */}
            <div className={`rounded-xl p-4 font-mono text-xs leading-relaxed overflow-y-auto max-h-80 ${darkMode ? 'bg-gray-900 text-green-400' : 'bg-gray-950 text-green-400'}`}>
              {progressModal.logs && progressModal.logs.length ? (
                progressModal.logs.map((log, i) => (
                  <div key={i} className="mb-3">
                    <div>
                      <span className="text-gray-500">{formatTimestamp(log.created_at)}</span>
                      <span className="text-green-400">: {log.log_text}</span>
                    </div>
                    {log.image_url && (
                      <img
                        src={log.image_url}
                        alt=""
                        onClick={() => setLightbox(log.image_url)}
                        className="mt-2 max-h-40 rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity"
                      />
                    )}
                  </div>
                ))
              ) : (
                <span className="text-gray-600">Noch keine Einträge.</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Beschreibungs-Modal */}
      {descModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setDescModal(null)}>
          <div
            className={`w-full max-w-lg sm:max-w-2xl lg:max-w-4xl rounded-2xl p-6 sm:p-8 ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl max-h-[80vh] overflow-y-auto`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{descModal.name}</h3>
              <button onClick={() => setDescModal(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {descModal.description}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="max-w-full max-h-full rounded-xl object-contain" />
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white text-3xl leading-none">&times;</button>
        </div>
      )}

      {/* Vormerken-Modal */}
      {vormerkModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={closeVormerken}>
          <div
            className={`w-full max-w-md sm:max-w-lg rounded-2xl p-6 sm:p-8 ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-bold ${text}`}>Vormerken</h3>
              <button onClick={closeVormerken} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>

            {emailSent ? (
              <div className="text-center py-4">
                <div className="text-green-500 text-4xl mb-3">✓</div>
                <p className={`font-medium ${text}`}>Du wirst benachrichtigt!</p>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Wir schicken dir eine E-Mail sobald es Neuigkeiten zu <strong>{vormerkModal.name}</strong> gibt.
                </p>
              </div>
            ) : (
              <>
                <p className={`mb-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Trag deine E-Mail ein um über Fortschritte zu <strong className={text}>{vormerkModal.name}</strong> informiert zu werden.
                </p>
                <input
                  type="email"
                  placeholder="deine@email.de"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && submitVormerken()}
                  className={`w-full px-4 py-3 rounded-xl border mb-3 outline-none focus:ring-2 focus:ring-blue-500 transition ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900'
                  }`}
                />
                {emailError && <p className="text-red-500 text-sm mb-3">{emailError}</p>}
                <button
                  onClick={submitVormerken}
                  className="w-full py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors duration-200"
                >
                  Vormerken
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
